"use client";

import { type ReactNode } from "react";
import { X } from "lucide-react";

import ImageWithFallback from "@/components/ImageWithFallback";
import { formatSizeInBytes, getFileImage } from "@/utils/generic";
import { getFileType } from "@/utils/mimetypes";
import { cn } from "@/lib/utils";

interface SelectedFileRowProps {
  name: string;
  contentType?: string;
  size?: number;
  onRemove?: () => void;
  isDisabled?: boolean;
  // Replaces the remove button, e.g. an upload progress or success indicator.
  trailing?: ReactNode;
  error?: string;
  className?: string;
}

export function SelectedFileRow({
  name,
  contentType,
  size,
  onRemove,
  isDisabled,
  trailing,
  error,
  className,
}: SelectedFileRowProps) {
  const meta = size
    ? `${getFileType(name)} · ${formatSizeInBytes(size)}`
    : getFileType(name);

  return (
    <div
      className={cn(
        "flex h-8 w-full min-w-0 shrink-0 items-center gap-2 rounded-md border border-input bg-transparent pl-2.5 pr-1 shadow-sm",
        error && "border-destructive/20 bg-destructive/10",
        className
      )}
    >
      <div className="relative size-4 shrink-0">
        <ImageWithFallback src={getFileImage(contentType)} alt={name} fill />
      </div>
      <span className="min-w-0 flex-1 truncate text-sm text-foreground">
        {name}
      </span>
      <span
        title={error}
        className={cn(
          "shrink-0 truncate text-xs",
          error ? "max-w-1/2 text-destructive" : "text-muted-foreground"
        )}
      >
        {error ?? meta}
      </span>
      {trailing ??
        (onRemove ? (
          <button
            type="button"
            disabled={isDisabled}
            onClick={onRemove}
            className={cn(
              "flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground/70",
              "transition-colors hover:bg-accent hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
              "disabled:cursor-not-allowed disabled:opacity-50"
            )}
            aria-label={`Remove ${name}`}
            title="Remove file"
          >
            <X size={14} strokeWidth={1.5} />
          </button>
        ) : null)}
    </div>
  );
}
